import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CreateApiKeyModal = ({ isOpen, onClose, onCreateKey }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'development',
    permissions: [],
    rateLimit: 1000,
    expiresAt: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const availablePermissions = [
    { id: 'leads:read', label: 'Read Leads', description: 'View lead data and lists' },
    { id: 'leads:write', label: 'Write Leads', description: 'Create and update leads' },
    { id: 'campaigns:read', label: 'Read Campaigns', description: 'View campaign data' },
    { id: 'campaigns:write', label: 'Write Campaigns', description: 'Create and manage campaigns' },
    { id: 'analytics:read', label: 'Read Analytics', description: 'Access analytics data' },
    { id: 'webhooks:write', label: 'Webhook Access', description: 'Receive webhook notifications' },
    { id: 'contacts:read', label: 'Read Contacts', description: 'View contact information' },
    { id: 'contacts:write', label: 'Write Contacts', description: 'Create and update contacts' },
    { id: 'templates:read', label: 'Read Templates', description: 'Access message templates' },
    { id: 'templates:write', label: 'Write Templates', description: 'Create and modify templates' }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.name?.trim()) {
      setError('API key name is required');
      return;
    }

    if (formData?.permissions?.length === 0) {
      setError('At least one permission must be selected');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await onCreateKey?.({
        name: formData?.name?.trim(),
        type: formData?.type,
        permissions: formData?.permissions,
        rateLimit: parseInt(formData?.rateLimit) || 1000,
        expiresAt: formData?.expiresAt || null
      });
      
      // Reset form
      setFormData({
        name: '',
        type: 'development',
        permissions: [],
        rateLimit: 1000,
        expiresAt: ''
      });
    } catch (err) {
      setError('Failed to create API key. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev?.permissions?.includes(permissionId)
        ? prev?.permissions?.filter(p => p !== permissionId)
        : [...prev?.permissions, permissionId]
    }));
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        type: 'development',
        permissions: [],
        rateLimit: 1000,
        expiresAt: ''
      });
      setError('');
      onClose?.();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white border border-gray-200 rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Create API Key</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="flex items-center"
            disabled={loading}
          >
            <span>✗</span>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <span className="text-red-600 mt-0.5">⚠️</span>
                <p className="text-red-800 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* API Key Name */}
          <div>
            <Input
              label="API Key Name"
              type="text"
              value={formData?.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e?.target?.value }))}
              placeholder="e.g., Production Integration, Mobile App, Webhook Handler"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Choose a descriptive name to help you identify this key later
            </p>
          </div>

          {/* API Key Type */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-2">Environment Type</label>
            <select
              value={formData?.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e?.target?.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="development">Development</option>
              <option value="production">Production</option>
              <option value="webhook">Webhook</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Development keys have lower rate limits, production keys are for live applications
            </p>
          </div>

          {/* Rate Limit */}
          <div>
            <Input
              label="Rate Limit (requests per hour)"
              type="number"
              value={formData?.rateLimit}
              onChange={(e) => setFormData(prev => ({ ...prev, rateLimit: e?.target?.value }))}
              min="100"
              max="10000"
              step="100"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Maximum number of API requests allowed per hour (100-10,000)
            </p>
          </div>

          {/* Expiration Date */}
          <div>
            <Input
              label="Expiration Date (Optional)"
              type="date"
              value={formData?.expiresAt}
              onChange={(e) => setFormData(prev => ({ ...prev, expiresAt: e?.target?.value }))}
              min={new Date()?.toISOString()?.split('T')?.[0]}
            />
            <p className="mt-1 text-xs text-gray-500">
              Leave blank for no expiration. You can always revoke the key manually.
            </p>
          </div>

          {/* Permissions */}
          <div>
            <label className="block text-sm font-medium text-gray-900 mb-3">Permissions</label>
            <div className="space-y-3 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4">
              {availablePermissions?.map((permission) => (
                <label key={permission?.id} className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                  <input
                    type="checkbox"
                    checked={formData?.permissions?.includes(permission?.id)}
                    onChange={() => handlePermissionToggle(permission?.id)}
                    className="mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{permission?.label}</p>
                    <p className="text-xs text-gray-600">{permission?.description}</p>
                  </div>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Selected: {formData?.permissions?.length} permissions
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button 
              type="button" 
              variant="ghost" 
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={loading || !formData?.name || formData?.permissions?.length === 0}
              className="flex items-center space-x-2"
            >
              {loading && <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />}
              <span>Create API Key</span>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateApiKeyModal;